import React, { FC, useCallback } from 'react';
import { FormRenderProps, Form as ReactForm } from 'react-final-form';

import { Config } from 'final-form';

import { Checkbox, Text } from '@nord/ui';

import { FormError } from '../../../../molecules/Form/FormError';
import Input from '../../../../molecules/Form/Input';
import PasswordField from '../../../../molecules/Form/PasswordField';
import Submit from '../../../../molecules/Form/Submit';
import { isSubmitDisabled } from '../../../../tools/isSubmitDisabled';
import { IsEmail, Required, composeValidators } from '../../../../validation/FormValidation';
import { initialState } from '../../../constants';

export interface ILoginFormValues {
  email: string;
  password: string;
  remember_me?: boolean;
}

export interface IProps {
  rememberMe?: boolean;
  isSubmitting?: boolean;
  onSubmit: Config<ILoginFormValues>['onSubmit'];
}

const LoginForm: FC<IProps> = ({
  rememberMe: remember_me = initialState.rememberMe,
  onSubmit,
  isSubmitting,
}) => {
  const initialValues: ILoginFormValues = { email: '', password: '', remember_me };

  const content = useCallback(
    ({ handleSubmit, ...restFormProps }: FormRenderProps<ILoginFormValues>) => (
      <form noValidate onSubmit={handleSubmit} data-testid="login-form">
        <Input
          id="LoginFormEmailInput"
          data-testid="login-email"
          placeholder="example@email.com"
          type="email"
          name="email"
          label="Email address"
          fieldProps={{ validate: composeValidators(Required, IsEmail) }}
        />
        <PasswordField
          name="password"
          label="Password"
          data-testid="login-password"
          placeholder="*********"
          required
          id="LoginFormPasswordInput"
          linkProps={{ id: 'LoginFormPasswordInputToggle' }}
        />
        <Text
          component="div"
          variant="caption"
          className="mt-4 font-medium flex justify-start items-center"
        >
          <Input
            name="remember_me"
            data-testid="login-remember-me"
            id="remember_me"
            component={Checkbox}
            fieldProps={{ type: 'checkbox' }}
            labelClassName="inline-flex items-center"
            label="Remember me"
          />
        </Text>
        <Submit
          data-testid="login-submit"
          loading={isSubmitting}
          id="LoginFormSubmitBtn"
          className="w-full mb-4"
          formProps={{ ...restFormProps }}
          disabled={isSubmitting || isSubmitDisabled(restFormProps)}
        >
          Log In
        </Submit>
        <FormError className="mb-4" />
      </form>
    ),
    [isSubmitting]
  );

  return <ReactForm initialValues={initialValues} onSubmit={onSubmit} render={content} />;
};

export default LoginForm;
